import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Loader2, Minus, Plus } from "lucide-react";
import { AssetNetwork, DEFAULT_PAGE_SIZE } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { StatusBadge } from "@/components/status-badge";
import { AssetLabel } from "@/components/asset-label";
import { formatUsdAmount } from "@/components/amount";
import { cn, formatStatusLabel } from "@/lib/utils";
import { createAsset, listAssets, updateAsset } from "@/api/assets";
import { ApiError } from "@/api/client";
import { type AssetListItem, type CreateAssetInput, type UpdateAssetInput } from "@/api/types";

// Real implementation of Task 29 Part B, replacing the Task-22 placeholder.
// `GET /v1/assets` has no pagination/sort/filter support on the backend
// (bare `{ data: AssetListItem[] }`, "every asset in one response" per its
// own doc comment) — same client-side-driven DataTable situation as Task
// 25's merchants list: fetch once via useQuery(["assets"], listAssets),
// derive the visible page from local page/pageSize state only.
//
// Reachable only via the "Assets & Rates" nav item, gated to CONFIG_MANAGER
// in _app.tsx's NAV_SECTIONS — every control on this page requires that same
// role, so (per the plan's Global Constraints) no further per-control
// hasRole() gating is needed here.
export const Route = createFileRoute("/_app/assets")({
  component: AssetsPage,
});

// Mirrors backoffice-api's dashboard-health.service.ts STALE_RATE_MINUTES
// constant (10) — same "stale" threshold as the dashboard health tile's own
// stale-rates list, computed client-side against Date.now() since this is
// live browser code rather than a backend query.
const STALE_RATE_MINUTES = 10;

function isRateStale(rateFetchedAt: string | null): boolean {
  if (rateFetchedAt === null) return true;
  return Date.now() - new Date(rateFetchedAt).getTime() > STALE_RATE_MINUTES * 60 * 1000;
}

const ERROR_BOX = "rounded-md border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 text-sm text-[#EF4444]";
const NOTE_BOX = "rounded-md border border-[#F59E0B]/30 bg-[#F59E0B]/5 px-3 py-2 text-xs text-[#92400E]";
const SELECT_CLASSNAME = cn(
  "h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm shadow-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
);

// A plain boolean isn't a domain state machine (no StatusBadge/status-colors
// entry warranted) — a small check-or-dash flag reads clearly for
// isNative/autoFuel without inventing a fake "enum".
function BooleanFlag({ value }: { value: boolean }) {
  return value ? (
    <Check className="h-4 w-4 text-[#10B981]" />
  ) : (
    <Minus className="h-4 w-4 text-muted-foreground" />
  );
}

// ─── create asset dialog ────────────────────────────────────────────────────
//
// Mirrors _app.merchants.tsx's CreateMerchantDialog pattern exactly:
// controlled useState fields, no react-hook-form, inline error box reading
// ApiError.message verbatim (the two distinct 409 messages
// AssetsService.create()'s isUniqueViolationOn() throws for the composite
// symbol+network index vs. the partial one-native-per-network index).

const DEFAULT_CREATE_STATE = {
  name: "",
  symbol: "",
  network: AssetNetwork.BITCOIN,
  decimals: 8,
  fireblocksAssetId: "",
  isNative: false,
  autoFuel: false,
  enabled: true,
};

function CreateAssetDialog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(DEFAULT_CREATE_STATE);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setForm(DEFAULT_CREATE_STATE);
    setError(null);
  };

  const createMutation = useMutation({
    mutationFn: () => {
      const input: CreateAssetInput = {
        name: form.name.trim(),
        symbol: form.symbol.trim(),
        network: form.network,
        decimals: form.decimals,
        fireblocksAssetId: form.fireblocksAssetId.trim(),
        isNative: form.isNative,
        autoFuel: form.autoFuel,
        enabled: form.enabled,
      };
      return createAsset(input);
    },
    onSuccess: () => {
      toast.success("Asset created.");
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      setOpen(false);
      reset();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Failed to create asset.");
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim() || !form.symbol.trim() || !form.fireblocksAssetId.trim()) {
      setError("Name, symbol, and Fireblocks asset ID are required.");
      return;
    }
    if (!Number.isInteger(form.decimals) || form.decimals < 0) {
      setError("Decimals must be a non-negative integer.");
      return;
    }
    createMutation.mutate();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4" />
          Create asset
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create asset</DialogTitle>
          <DialogDescription>Adds a new supported asset to the catalogue.</DialogDescription>
        </DialogHeader>

        {error && <div className={ERROR_BOX}>{error}</div>}

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="asset-name">Name</Label>
              <Input
                id="asset-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="asset-symbol">Symbol</Label>
              <Input
                id="asset-symbol"
                value={form.symbol}
                onChange={(e) => setForm((f) => ({ ...f, symbol: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="asset-network">Network</Label>
              <select
                id="asset-network"
                className={SELECT_CLASSNAME}
                value={form.network}
                onChange={(e) => setForm((f) => ({ ...f, network: e.target.value as AssetNetwork }))}
              >
                {Object.values(AssetNetwork).map((network) => (
                  <option key={network} value={network}>
                    {formatStatusLabel(network)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="asset-decimals">Decimals</Label>
              <Input
                id="asset-decimals"
                type="number"
                min={0}
                step={1}
                value={form.decimals}
                onChange={(e) => setForm((f) => ({ ...f, decimals: Number(e.target.value) }))}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="asset-fireblocksId">Fireblocks asset ID</Label>
            <Input
              id="asset-fireblocksId"
              value={form.fireblocksAssetId}
              onChange={(e) => setForm((f) => ({ ...f, fireblocksAssetId: e.target.value }))}
            />
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 cursor-pointer rounded border-[#D1D5DB] accent-[#0F172A]"
                checked={form.isNative}
                onChange={(e) => setForm((f) => ({ ...f, isNative: e.target.checked }))}
              />
              Native asset
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 cursor-pointer rounded border-[#D1D5DB] accent-[#0F172A]"
                checked={form.autoFuel}
                onChange={(e) => setForm((f) => ({ ...f, autoFuel: e.target.checked }))}
              />
              Auto-fuel
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 cursor-pointer rounded border-[#D1D5DB] accent-[#0F172A]"
                checked={form.enabled}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
              Enabled
            </label>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={createMutation.isPending} className="w-full sm:w-auto">
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create asset"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── edit asset dialog ──────────────────────────────────────────────────────
//
// name/fireblocksAssetId/autoFuel/enabled are always editable.
// symbol/network/isNative aren't rendered at all — not patchable per
// UpdateAssetDto. decimals is rendered but disabled while `referenced`, with
// an inline note (client-gated to match the server's own 409 guard, the same
// "match server enforcement" principle Task 25 applied to roles).

function EditAssetDialog({ asset }: { asset: AssetListItem }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(asset.name);
  const [fireblocksAssetId, setFireblocksAssetId] = useState(asset.fireblocksAssetId);
  const [decimals, setDecimals] = useState(asset.decimals);
  const [autoFuel, setAutoFuel] = useState(asset.autoFuel);
  const [enabled, setEnabled] = useState(asset.enabled);
  const [error, setError] = useState<string | null>(null);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      // Reset from the latest asset row every time the dialog opens.
      setName(asset.name);
      setFireblocksAssetId(asset.fireblocksAssetId);
      setDecimals(asset.decimals);
      setAutoFuel(asset.autoFuel);
      setEnabled(asset.enabled);
      setError(null);
    }
  };

  const mutation = useMutation({
    mutationFn: () => {
      const input: UpdateAssetInput = {
        name: name.trim(),
        fireblocksAssetId: fireblocksAssetId.trim(),
        autoFuel,
        enabled,
      };
      // Only sent while unreferenced — the backend 409s if `decimals` is
      // present at all on a referenced asset, even when the value is
      // unchanged, so it must be omitted entirely rather than resent.
      if (!asset.referenced) {
        input.decimals = decimals;
      }
      return updateAsset(asset.id, input);
    },
    onSuccess: () => {
      toast.success("Asset updated.");
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      setOpen(false);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Failed to update asset.");
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !fireblocksAssetId.trim()) {
      setError("Name and Fireblocks asset ID are required.");
      return;
    }
    if (!asset.referenced && (!Number.isInteger(decimals) || decimals < 0)) {
      setError("Decimals must be a non-negative integer.");
      return;
    }
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {asset.symbol}</DialogTitle>
          <DialogDescription>
            Updates this asset&apos;s name, Fireblocks asset ID, auto-fuel, and enabled state.
          </DialogDescription>
        </DialogHeader>

        {error && <div className={ERROR_BOX}>{error}</div>}

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-asset-name">Name</Label>
            <Input id="edit-asset-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-asset-fireblocksId">Fireblocks asset ID</Label>
            <Input
              id="edit-asset-fireblocksId"
              value={fireblocksAssetId}
              onChange={(e) => setFireblocksAssetId(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-asset-decimals">Decimals</Label>
            <Input
              id="edit-asset-decimals"
              type="number"
              min={0}
              step={1}
              value={decimals}
              disabled={asset.referenced}
              onChange={(e) => setDecimals(Number(e.target.value))}
            />
            {asset.referenced && (
              <p className={NOTE_BOX}>
                Cannot be changed — this asset is already referenced by invoices, accounts, wallets, or
                tariff rules.
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 cursor-pointer rounded border-[#D1D5DB] accent-[#0F172A]"
                checked={autoFuel}
                onChange={(e) => setAutoFuel(e.target.checked)}
              />
              Auto-fuel
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 cursor-pointer rounded border-[#D1D5DB] accent-[#0F172A]"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              Enabled
            </label>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending} className="w-full sm:w-auto">
              {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── assets page ─────────────────────────────────────────────────────────────

interface ListState {
  page: number;
  pageSize: number;
}

function AssetsPage() {
  const queryClient = useQueryClient();

  const { data, isPending, isError } = useQuery({
    queryKey: ["assets"],
    queryFn: listAssets,
  });

  const [state, setState] = useState<ListState>({ page: 1, pageSize: DEFAULT_PAGE_SIZE });

  // Quick inline enable/disable toggle — reuses the exact same updateAsset
  // call the edit dialog's own mutation makes, just as a separate useMutation
  // instance (toast feedback, no dialog) rather than a third code path. Same
  // "dedicated toggle + dialog share one API function, not one hook" split
  // as the merchant detail page's OverviewTab accessMutation vs.
  // EditProfileDialog.
  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => updateAsset(id, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      toast.success("Asset updated.");
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "Failed to update asset.");
    },
  });

  const allItems = data?.data ?? [];
  const total = allItems.length;
  const pageItems = allItems.slice((state.page - 1) * state.pageSize, state.page * state.pageSize);

  const columns: DataTableColumn<AssetListItem>[] = [
    {
      key: "asset",
      header: "Asset",
      cell: (a) => <AssetLabel symbol={a.symbol} network={a.network} />,
    },
    {
      key: "fireblocksAssetId",
      header: "Fireblocks ID",
      cell: (a) => <span className="font-mono text-xs text-muted-foreground">{a.fireblocksAssetId}</span>,
    },
    { key: "isNative", header: "Native", cell: (a) => <BooleanFlag value={a.isNative} /> },
    { key: "autoFuel", header: "Auto-fuel", cell: (a) => <BooleanFlag value={a.autoFuel} /> },
    {
      key: "enabled",
      header: "Status",
      cell: (a) => (
        <StatusBadge label={a.enabled ? "Enabled" : "Disabled"} color={a.enabled ? "success" : "neutral"} />
      ),
    },
    { key: "rate", header: "Rate", cell: (a) => formatUsdAmount(a.rate) },
    {
      key: "rateSource",
      header: "Rate source",
      cell: (a) => <span className="text-muted-foreground">{a.rateSource ?? "—"}</span>,
    },
    {
      key: "rateFetchedAt",
      header: "Rate status",
      cell: (a) => (
        <StatusBadge
          label={isRateStale(a.rateFetchedAt) ? "Stale" : "Fresh"}
          color={isRateStale(a.rateFetchedAt) ? "danger" : "success"}
        />
      ),
    },
    {
      key: "actions",
      header: "Actions",
      cell: (a) => (
        <div className="flex items-center gap-2">
          <EditAssetDialog asset={a} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={toggleMutation.isPending}
            onClick={() => toggleMutation.mutate({ id: a.id, enabled: !a.enabled })}
          >
            {a.enabled ? "Disable" : "Enable"}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <CreateAssetDialog />
      </div>

      <DataTable
        columns={columns}
        items={pageItems}
        total={total}
        page={state.page}
        pageSize={state.pageSize}
        loading={isPending}
        error={isError ? "Failed to load assets." : null}
        onPageChange={(page) => setState((s) => ({ ...s, page }))}
        onPageSizeChange={(pageSize) => setState((s) => ({ ...s, pageSize, page: 1 }))}
        getRowKey={(a) => a.id}
        emptyMessage="No assets yet."
      />
    </div>
  );
}
